
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import api from "../../../services/api";
import { buildImageUrl } from "../../../config/config";
import { Helmet } from "react-helmet";
import ImageCropModal from "../../../components/ImageCropModal";

function EditBlog() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    const [metaKeywordsEdited, setMetaKeywordsEdited] = useState(false);
    const [imageAltEdited, setImageAltEdited] = useState(false);
    const [h1Edited, setH1Edited] = useState(false);
    const [h2Edited, setH2Edited] = useState(false);

    const [formData, setFormData] = useState({
        title: "", description: "", url: "", status: "active",
        image: null, existing_image_url: "", tag: "", short_desc: "",
        meta_desc: "", h1_tag: "", h2_tag: "", bold_tag: "",
        italic_tag: "", blog_url: "", image_alt: ""
    });

    const [formErrors, setFormErrors] = useState({});
    const [backendMessage, setBackendMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const editorRef = useRef(null);
    const editorInstanceRef = useRef(null);
    const imageInputRef = useRef(null);

    const descriptionRef = useRef("");

    useEffect(() => {
        descriptionRef.current = formData.description;
    }, [formData.description]);

    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState("");
    const [cropFileName, setCropFileName] = useState("");
    const [cropFileType, setCropFileType] = useState("");

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                console.log("Fetching blog data for ID:", id);
                const response = await api.get(`/bloglist/${id}`);
                console.log("Blog API Response:", response.data);
                
                const blogData = response.data.item || response.data.blog || response.data;
                console.log("Extracted blog data:", blogData);

                // Map backend fields to frontend form fields
                // Backend: title, slug, excerpt, content, author, publishDate, readTime, category, tags (array), image, featured
                // Frontend: title, description (content), url (slug), status (featured), image, existing_image_url (image), tag (tags), short_desc (excerpt), meta_desc, h1_tag, h2_tag, bold_tag, italic_tag, blog_url (slug)
                
                const mappedFormData = {
                    title: blogData.title || "",
                    description: blogData.content || blogData.description || "", // Map content to description
                    url: blogData.slug || blogData.url || "", // Map slug to url
                    status: blogData.featured ? "featured" : (blogData.status || "active"), // Map featured to status
                    image: null, // Always null for file input
                    existing_image_url: blogData.image || blogData.image_url || "", // Map image to existing_image_url
                    tag: Array.isArray(blogData.tags) ? blogData.tags.join(", ") : (blogData.tag || blogData.tags || ""), // Convert tags array to comma-separated string
                    short_desc: blogData.excerpt || blogData.short_desc || "", // Map excerpt to short_desc
                    meta_desc: blogData.meta_desc || blogData.metaDescription || "",
                    h1_tag: blogData.h1_tag || blogData.title || "",
                    h2_tag: blogData.h2_tag || blogData.meta_desc || blogData.metaDescription || "",
                    bold_tag: blogData.bold_tag || "",
                    italic_tag: blogData.italic_tag || "",
                    blog_url: blogData.slug || blogData.blog_url || "",
                    image_alt: blogData.image_alt || ""
                };

                const normalizedTag = String(mappedFormData.tag || "").trim();
                const normalizedAlt = String(mappedFormData.image_alt || "").trim();
                if (normalizedTag && !normalizedAlt) mappedFormData.image_alt = normalizedTag;
                if (!normalizedTag && normalizedAlt) mappedFormData.tag = normalizedAlt;

                setMetaKeywordsEdited(false);
                setImageAltEdited(false);
                setH1Edited(Boolean(mappedFormData.h1_tag && mappedFormData.title && mappedFormData.h1_tag !== mappedFormData.title));
                setH2Edited(Boolean(mappedFormData.h2_tag && mappedFormData.meta_desc && mappedFormData.h2_tag !== mappedFormData.meta_desc));
                
                console.log("Mapped form data:", mappedFormData);
                setFormData(mappedFormData);
                
                // Update CKEditor content if it's already initialized
                if (editorInstanceRef.current) {
                    const content = mappedFormData.description || blogData.content || blogData.description || '';
                    console.log("Setting CKEditor content:", content);
                    editorInstanceRef.current.setData(content);
                }
            } catch (err) {
                console.error("Error loading blog:", err.response?.data || err.message);
                setBackendMessage({ type: 'danger', text: 'Failed to load blog for editing.' });
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchBlog();
        }
    }, [id]);

    const initEditor = useCallback(async () => {
        if (!editorRef.current) {
            setTimeout(initEditor, 50);
            return;
        }

        if (editorInstanceRef.current) {
            const content = descriptionRef.current;
            if (content) {
                editorInstanceRef.current.setData(content);
            }
            return;
        }

        try {
            const editor = await window.ClassicEditor.create(editorRef.current);
            editorInstanceRef.current = editor;

            const content = descriptionRef.current || "";
            console.log("Initializing CKEditor with content:", content);
            editor.setData(content);

            editor.model.document.on("change:data", () => {
                setFormData(prev => ({
                    ...prev,
                    description: editor.getData()
                }));
            });
        } catch (err) {
            console.error("CKEditor initialization failed:", err);
        }
    }, []);

    useEffect(() => {
        const loadEditor = () => {
            if (!window.ClassicEditor) {
                const script = document.createElement("script");
                script.src = "https://cdn.ckeditor.com/ckeditor5/37.0.1/classic/ckeditor.js";
                script.onload = initEditor;
                document.body.appendChild(script);
            } else {
                initEditor();
            }
        };

        if (!loading) {
            loadEditor();
        }

        return () => {
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy().catch(console.error);
                editorInstanceRef.current = null;
            }
        };
    }, [loading, initEditor]);
    
    // Separate effect to update CKEditor when formData.description changes (after initial load)
    useEffect(() => {
        if (editorInstanceRef.current && !loading && formData.description) {
            // Only update if content is different to avoid infinite loops
            const currentContent = editorInstanceRef.current.getData();
            if (currentContent !== formData.description) {
                console.log("Updating CKEditor content:", formData.description);
                editorInstanceRef.current.setData(formData.description);
            }
        }
    }, [formData.description, loading]);


    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (name === 'title') {
            setFormData(prev => {
                const next = { ...prev, title: value };
                if (!id) {
                    next.h1_tag = h1Edited ? prev.h1_tag : value;
                }
                return next;
            });
            if (formErrors.title) setFormErrors(prev => ({ ...prev, title: "" }));
            return;
        }

        if (name === 'h1_tag') {
            setH1Edited(true);
            setFormData(prev => ({ ...prev, h1_tag: value }));
            if (formErrors.h1_tag) setFormErrors(prev => ({ ...prev, h1_tag: "" }));
            return;
        }

        if (name === 'meta_desc') {
            setFormData(prev => {
                const next = { ...prev, meta_desc: value };
                if (!id) {
                    next.h2_tag = h2Edited ? prev.h2_tag : value;
                }
                return next;
            });
            if (formErrors.meta_desc) setFormErrors(prev => ({ ...prev, meta_desc: "" }));
            return;
        }

        if (name === 'h2_tag') {
            setH2Edited(true);
            setFormData(prev => ({ ...prev, h2_tag: value }));
            if (formErrors.h2_tag) setFormErrors(prev => ({ ...prev, h2_tag: "" }));
            return;
        }

        if (name === 'tag') {
            setMetaKeywordsEdited(true);
            setFormData(prev => {
                return {
                    ...prev,
                    tag: value,
                    image_alt: imageAltEdited ? prev.image_alt : value
                };
            });
            if (formErrors.tag) setFormErrors(prev => ({ ...prev, tag: "" }));
            return;
        }

        if (name === 'image_alt') {
            setImageAltEdited(true);
            setFormData(prev => {
                return {
                    ...prev,
                    image_alt: value,
                    tag: metaKeywordsEdited ? prev.tag : value
                };
            });
            if (formErrors.image_alt) setFormErrors(prev => ({ ...prev, image_alt: "" }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === "file" ? files[0] : value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const previewImage = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const nextSrc = typeof reader.result === "string" ? reader.result : "";
                setCropImageSrc(nextSrc);
                setCropFileName(file.name || "image");
                setCropFileType(file.type || "image/jpeg");
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const previewSrc = (existing) => {
        if (!existing) return '';
        if (typeof existing === 'string' && (existing.startsWith('data:') || existing.startsWith('blob:'))) {
            return existing;
        }
        try {
            return buildImageUrl(existing);
        } catch {
            return existing;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setFormErrors({});
        setBackendMessage({ type: '', text: '' });

        const formToSend = new FormData();
        formToSend.append('title', formData.title || '');
        formToSend.append('description', editorInstanceRef.current ? editorInstanceRef.current.getData() : (formData.description || ''));
        formToSend.append('url', formData.url || '');
        formToSend.append('blog_url', formData.blog_url || '');
        formToSend.append('status', formData.status || '');
        formToSend.append('tag', formData.tag || '');
        formToSend.append('short_desc', formData.short_desc || '');
        formToSend.append('meta_desc', formData.meta_desc || '');
        formToSend.append('h1_tag', formData.h1_tag || '');
        formToSend.append('h2_tag', formData.h2_tag || '');
        formToSend.append('bold_tag', formData.bold_tag || '');
        formToSend.append('italic_tag', formData.italic_tag || '');
        formToSend.append('image_alt', formData.image_alt || '');

        if (formData.image) {
            formToSend.append('image', formData.image);
        } else if (formData.existing_image_url) {
            formToSend.append('existing_image_url', formData.existing_image_url);
        }

        try {
            await api.patch(`/bloglist/update/${id}`, formToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setBackendMessage({ type: 'success', text: 'Blog edit successfully!' });
            setTimeout(() => navigate('/admin/bloglist'), 1500);
        } catch (err) {
            if (err.response?.status === 422) {
                const errors = {};
                err.response.data.errors.forEach(e => {
                    errors[e.param] = e.msg;
                });
                setFormErrors(errors);
                setBackendMessage({ type: 'danger', text: 'Please fix validation errors.' });
            } else {
                setBackendMessage({ type: 'danger', text: 'Update failed. Try again later.' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="content-wrapper"><p>Loading blog data...</p></div>;

    return (
        <>
            <Helmet>
                <title>Admin | {id ? "Edit Blog Details" : "Add Blog Details"}</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <Link className="breadcrumb-link" to="/admin/bloglist">Blog</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">{id ? "Edit Blog" : "Add Blog"}</div>
                            </div>
                        </div>

                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-header">{id ? "Edit Blog" : "Add Blog"}</div>
                                        <div className="card-body">
                                            {backendMessage.text && (
                                                <div className={`alert alert-${backendMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
                                                    {backendMessage.text}
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit} encType="multipart/form-data">
                                                <div className="row">
                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="title">Title</label>
                                                        <input type="text" className={`form-control ${formErrors.title ? 'is-invalid' : ''}`} id="title" placeholder="Enter title" name="title" value={formData.title} onChange={handleChange} />
                                                        {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="url">Url</label>
                                                        <input type="text" className={`form-control ${formErrors.url ? 'is-invalid' : ''}`} id="url" placeholder="Enter url" name="url" value={formData.url} onChange={handleChange} />
                                                        {formErrors.url && <div className="invalid-feedback">{formErrors.url}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="tag">Meta Keywords</label>
                                                        <input type="text" className="form-control" id="tag" placeholder="Enter Tag" name="tag" value={formData.tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="short_desc">Short Description</label>
                                                        <input type="text" className="form-control" id="short_desc" placeholder="Enter short description" name="short_desc" value={formData.short_desc} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="image">Image</label>
                                                        <input ref={imageInputRef} type="file" className={`form-control ${formErrors.image ? 'is-invalid' : ''}`} id="image" name="image" onChange={previewImage} accept="image/jpeg,image/png,image/jpg" />
                                                        <div className="input-note-2 mt-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"></path></svg>
                                                        </div>
                                                        {formErrors.image && <div className="invalid-feedback">{formErrors.image}</div>}
                                                        {formData.existing_image_url && (
                                                            <img src={previewSrc(formData.existing_image_url)} alt="Preview" style={{ maxWidth: '100px', marginTop: '10px', display: 'block' }} />
                                                        )}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="status">Status</label>
                                                        <select className={`form-control ${formErrors.status ? 'is-invalid' : ''}`} id="status" name="status" value={formData.status} onChange={handleChange}>
                                                            <option value="">Select Status</option>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="draft">Draft</option>
                                                            <option value="featured">Featured</option>
                                                        </select>
                                                        {formErrors.status && <div className="invalid-feedback">{formErrors.status}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="description">Description</label>
                                                        <textarea ref={editorRef} className={`form-control ${formErrors.description ? 'is-invalid' : ''}`} id="description" placeholder="Enter Description" name="description"></textarea>
                                                        {formErrors.description && <div className="invalid-feedback">{formErrors.description}</div>}
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="meta_desc">Meta Description</label>
                                                        <input type="text" className="form-control" id="meta_desc" placeholder="Enter meta description" name="meta_desc" value={formData.meta_desc} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="h1_tag">H1 Tags</label>
                                                        <input type="text" className="form-control" id="h1_tag" placeholder="Enter H1 tags" name="h1_tag" value={formData.h1_tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="h2_tag">H2 Tags</label>
                                                        <input type="text" className="form-control" id="h2_tag" placeholder="Enter H2 tags" name="h2_tag" value={formData.h2_tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="image_alt">Image Alt Text (used as meta keywords if tag empty)</label>
                                                        <input type="text" className="form-control" id="image_alt" placeholder="Enter image alt text" name="image_alt" value={formData.image_alt} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="bold_tag">Bold Tags</label>
                                                        <input type="text" className="form-control" id="bold_tag" placeholder="Enter bold tags" name="bold_tag" value={formData.bold_tag} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="italic_tag">Italic Tags</label>
                                                        <input type="text" className="form-control" id="italic_tag" placeholder="Enter italic tags" name="italic_tag" value={formData.italic_tag} onChange={handleChange} />
                                                    </div>

                                                    {/* <div className="form-group col-sm-6">
                                                        <label htmlFor="blog_url">Blog URL (Optional)</label>
                                                        <input type="text" className="form-control" id="blog_url" placeholder="Enter blog URL" name="blog_url" value={formData.blog_url} onChange={handleChange} />
                                                    </div> */}
                                                </div>

                                                <button type="submit" className="common-submit-btn mt-2" disabled={submitting}>
                                                    {submitting ? 'Submitting...' : (id ? 'Update Blog' : 'Add Blog')}
                                                </button>
                                                {/* <Link to="/admin/bloglist" className="btn btn-secondary ms-2">Cancel</Link> */}
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>

            <ImageCropModal
                isOpen={cropModalOpen}
                imageSrc={cropImageSrc}
                fileName={cropFileName}
                fileType={cropFileType}
                aspect={16 / 9}
                onClose={() => {
                    setCropModalOpen(false);
                    setCropImageSrc("");
                    if (imageInputRef.current) imageInputRef.current.value = "";
                }}
                onCropped={async ({ file, previewUrl }) => {
                    setFormData((prev) => ({
                        ...prev,
                        image: file,
                        existing_image_url: previewUrl,
                    }));
                    if (imageInputRef.current) imageInputRef.current.value = "";
                }}
            />
        </>
    );
}

export default EditBlog;
